/**
 * A treatment's identifying facts as icon-led readouts: quantity, stock batch
 * numbers, prescribed refills, instructions, tag number and injection site.
 */
import { Hash, MapPin, NotebookPen, RefreshCw, ShoppingBasket, Syringe, Tag } from 'lucide-react'
import type { ReactNode } from 'react'

import { formatRefillInterval, type RefillInterval } from '@snootsvet/shared'

import { cn } from '@/frontend/lib/utils'

interface TreatmentAttributesProps {
  /** Quantity dispensed for the owner to take home. */
  dispensedQuantity?: string | null
  /** Quantity given while the patient was in the consult. */
  administeredQuantity?: string | null
  /** Lots drawn from when stock was deducted at administration. */
  batchNumbers?: (string | null)[] | null
  repeatsAllowed?: number | null
  refillInterval?: RefillInterval | null
  instructions?: string | null
  tagNumber?: string | null
  injectionSite?: string | null
  className?: string
  children?: ReactNode
}

export function TreatmentAttributes({
  dispensedQuantity,
  administeredQuantity,
  batchNumbers,
  repeatsAllowed,
  refillInterval,
  instructions,
  tagNumber,
  injectionSite,
  className,
  children,
}: TreatmentAttributesProps) {
  const refills = repeatsAllowed
    ? `${repeatsAllowed} ${repeatsAllowed === 1 ? 'refill' : 'refills'}${
        refillInterval ? ` (${formatRefillInterval(refillInterval)} cadence)` : ''
      }`
    : null
  const batches = [...new Set(batchNumbers?.filter((b) => b != null) ?? [])]

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-normal text-foreground/65',
        className
      )}
    >
      {administeredQuantity && (
        <span title="Administered quantity" className="flex items-center gap-1">
          <Syringe className="size-3 shrink-0" />
          {administeredQuantity}
        </span>
      )}
      {dispensedQuantity && (
        <span title="Dispense quantity" className="flex items-center gap-1">
          <ShoppingBasket className="size-3 shrink-0" />
          {dispensedQuantity}
        </span>
      )}
      {batches.map((batchNumber) => (
        <span key={batchNumber} title="Batch/lot number" className="flex items-center gap-1">
          <Hash className="size-3 shrink-0" />
          {batchNumber}
        </span>
      ))}
      {refills && (
        <span title="Refills prescribed" className="flex items-center gap-1">
          <RefreshCw className="size-3 shrink-0" />
          {refills}
        </span>
      )}
      {tagNumber?.trim() && (
        <span title="Vaccination tag number" className="flex items-center gap-1">
          <Tag className="size-3 shrink-0" />
          {tagNumber}
        </span>
      )}
      {injectionSite?.trim() && (
        <span title="Injection site" className="flex items-center gap-1">
          <MapPin className="size-3 shrink-0" />
          {injectionSite}
        </span>
      )}
      {instructions?.trim() && (
        <span title="Instructions" className="flex items-start gap-1">
          <NotebookPen className="mt-[0.2rem] size-3 shrink-0" />
          <span>{instructions}</span>
        </span>
      )}
      {children}
    </div>
  )
}
